import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import useAuth from "../../../../../hooks/useAuth.js";
import { usePermissions } from "../../../../../contexts/permissionsContext.js";
import { useReportLinks } from "../../../../../contexts/reportLinksContext.js";
import { buildRelatoriosMenuChildren } from "../../../../lib/build-relatorios-menu-children.js";
import {
  FOOTER_ITEMS,
  MENU_ITEMS,
} from "../constants/index.js";
import { isAdmin } from "../utils/permissions.js";
import { SidebarProvider as UISidebarProvider } from "../../../ui/sidebar.js";
import { useIsMobile } from "../../../../hooks/use-mobile.js";
import { useNavigation } from "../../../../hooks/use-navigation.js";
import { cn } from "../../../../lib/utils.js";
import { SidebarContent } from "./sidebar-content.js";
import { SidebarFooter } from "./sidebar-footer.js";
import { SidebarHeader } from "./sidebar-header.js";
import { SIDEBAR_UI_CONFIG } from "../constants/sidebar-ui-config.js";

/**
 * Sidebar do layout (orquestração + composição).
 */
export function Sidebar({
  activeMenu,
  setActiveMenu,
  sidebarOpen,
  setSidebarOpen,
  collapsed,
  setCollapsed,
}) {
  const location = useLocation();
  const pathname = location.pathname;
  const isMobile = useIsMobile();
  const { navigate } = useNavigation();
  const { signout } = useAuth();
  const [isCollapsedLogoHovered, setIsCollapsedLogoHovered] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const { reportLinks } = useReportLinks();
  const { permissions: userRoles, hasPermissionAction } = usePermissions();

  useEffect(() => {
    if (isMobile) {
      if (sidebarOpen) {
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
      } else {
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.width = "";
      }
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [sidebarOpen, isMobile]);

  const isMenuItemVisible = useCallback(
    (item) => {
      if (item.requiresAdmin) {
        return isAdmin(userRoles);
      }

      if (item.permission) {
        return hasPermissionAction(item.permission, item.action || "islist");
      }

      return true;
    },
    [userRoles, hasPermissionAction],
  );

  const filteredMenuItems = useMemo(
    () =>
      MENU_ITEMS.reduce((acc, item) => {
        if (item.id === "relatorios" && item.children) {
          const children = buildRelatoriosMenuChildren(
            item.children,
            reportLinks,
            userRoles,
          );
          const visibleChildren = children.filter(isMenuItemVisible);
          if (visibleChildren.length === 0) {
            return acc;
          }
          acc.push({ ...item, children: visibleChildren });
          return acc;
        }

        if (item.children?.length) {
          const visibleChildren = item.children.filter(isMenuItemVisible);
          if (visibleChildren.length === 0) {
            return acc;
          }
          acc.push({ ...item, children: visibleChildren });
          return acc;
        }

        if (isMenuItemVisible(item)) {
          acc.push(item);
        }

        return acc;
      }, []),
    [reportLinks, userRoles, isMenuItemVisible],
  );

  const filteredFooterItems = useMemo(
    () => FOOTER_ITEMS.filter(isMenuItemVisible),
    [isMenuItemVisible],
  );

  const isPathActive = (href) =>
    pathname === href || (href === "/" && pathname === "/");

  const isSectionOpen = (item) => {
    if (openSections[item.id] !== undefined) {
      return openSections[item.id];
    }
    return item.children.some((child) => isPathActive(child.href));
  };

  const handleSectionOpenChange = (item, open) => {
    setOpenSections((prev) => ({ ...prev, [item.id]: open }));
  };

  const handleNavigation = (href, label) => {
    if (pathname !== href) {
      setSidebarOpen(false);
      navigate(href);
    }
  };

  const handleOverlayClick = () => {
    setSidebarOpen(false);
  };

  const handleSidebarClick = (e) => {
    e.stopPropagation();
  };

  const handleLogout = () => {
    signout?.();
    setSidebarOpen(false);
  };

  const handleExpandedLogoClick = () => {
    handleNavigation(SIDEBAR_UI_CONFIG.header.homeHref, "Home");
  };

  const sidebarEnhancedStyles = `
    @media (min-width: 1024px) {
      .sidebar-nav {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      .sidebar-nav::-webkit-scrollbar {
        display: none;
      }
    }
  `;

  return (
    <>
      <style>{sidebarEnhancedStyles}</style>

      {isMobile && (
        <div
          className="sidebar-overlay fixed inset-0 z-[1399] bg-black/60 lg:hidden"
          data-open={sidebarOpen}
          onClick={handleOverlayClick}
          style={{
            zIndex: sidebarOpen ? 1399 : -1,
            opacity: sidebarOpen ? 1 : 0,
            visibility: sidebarOpen ? "visible" : "hidden",
            pointerEvents: sidebarOpen ? "auto" : "none",
            transition: "opacity 250ms ease, visibility 250ms ease",
          }}
        />
      )}

      <div
        className={cn(
          "sidebar-panel dark flex h-screen flex-col bg-black text-white",
          isMobile ? "fixed top-0 left-0 w-64" : "sticky top-0",
          collapsed && !isMobile ? "w-16" : "w-64",
          sidebarOpen || !isMobile ? "translate-x-0" : "-translate-x-full",
        )}
        style={{
          zIndex: isMobile ? (sidebarOpen ? 1400 : -1) : 1500,
          transition:
            "transform 320ms cubic-bezier(0.4, 0, 0.2, 1), width 240ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms ease",
        }}
        data-open={sidebarOpen || !isMobile}
        onClick={handleSidebarClick}
      >
        <UISidebarProvider className="contents">
          <SidebarHeader
            collapsed={collapsed}
            isMobile={isMobile}
            isCollapsedLogoHovered={isCollapsedLogoHovered}
            onCollapsedLogoHoverEnter={() => setIsCollapsedLogoHovered(true)}
            onCollapsedLogoHoverLeave={() => setIsCollapsedLogoHovered(false)}
            onCollapsedLogoFocus={() => setIsCollapsedLogoHovered(true)}
            onCollapsedLogoBlur={() => setIsCollapsedLogoHovered(false)}
            onCollapse={() => setCollapsed(true)}
            onExpand={() => setCollapsed(false)}
            onCloseMobile={() => setSidebarOpen(false)}
            onExpandedLogoClick={handleExpandedLogoClick}
          />

          <SidebarContent
            items={filteredMenuItems}
            collapsed={collapsed}
            isMobile={isMobile}
            isPathActive={isPathActive}
            isSectionOpen={isSectionOpen}
            onSectionOpenChange={handleSectionOpenChange}
            onNavigate={handleNavigation}
            onActiveMenuChange={setActiveMenu}
          />

          <SidebarFooter
            footerItems={filteredFooterItems}
            collapsed={collapsed}
            isMobile={isMobile}
            onNavigate={handleNavigation}
            onLogout={handleLogout}
          />
        </UISidebarProvider>
      </div>
    </>
  );
}
